import React from "react";
import PropTypes from "prop-types";
import autoBind from "react-autobind";

/*
https://developers.google.com/maps/documentation/javascript/examples/overlay-simple

Note this component is unlike its peer <GroundOVerlay />
from react-google-maps in that it will rerender with new props.
This comp is meant to replace that component, offering the ability
to rotate the image.
*/

class ImageOverlay extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.overlays = [];
  }

  shouldComponentUpdate(nextProps) {
    // Because this redraws the image on the map
    // with each render, it can be flashy, unlike
    // most react components where the diff algo
    // can handle this. Only update if the
    // specific props that modify the overlay
    // have actually *changed*
    const {image, bounds, heading, opacity} = this.props;
    const {image: newImage, bounds: newBounds, heading: newHeading, opacity: newOpacity} = nextProps;
    if (image !== newImage || heading !== newHeading || opacity !== newOpacity) {
      return true;
    } else if (!bounds.equals(newBounds)) {
      // https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBounds.equals
      return true;
    }
    return false;
  }

  renderOverlay() {
    // The draw() method of ImageOverlayView is called only by google maps
    // internally, we cannot call it. In order to "rotate", or otherwise
    // rerender the component, you must remove (setMap(null)) the
    // old overlay and create a new overlay instance. This causes the
    // image to be flashy when rotating (or rendering fast for whatever reason)
    // In order to combat this flash, we render the new overlay before
    // deleting the last one after a delay.
    // Works pretty well, all things considered...
    const {bounds, image, heading, map, opacity} = this.props;
    this.overlays = [...this.overlays, new ImageOverlayView(map, bounds, image, heading, opacity)];
    if (this.overlays.length > 1) {
      setTimeout(() => {
        this.overlays[0].setMap(null);
        this.overlays = this.overlays.slice(1);
      }, 50);
    }
  }

  componentWillUnmount() {
    this.overlays.forEach(overlay => overlay.setMap(null));
  }

  render() {
    this.renderOverlay();
    return null;
  }
}

ImageOverlay.propTypes = {
  image: PropTypes.string.isRequired,
  bounds: PropTypes.object.isRequired,
  map: PropTypes.object.isRequired,
  heading: PropTypes.number,
  opacity: PropTypes.number
};

export default ImageOverlay;

ImageOverlayView.prototype = new window.google.maps.OverlayView();

function ImageOverlayView(map, bounds, image, heading = 0, opacity = 1) {
  // Initialize all properties.
  this.map_ = map;
  this.bounds_ = bounds;
  this.image_ = image;
  this.heading_ = heading;
  this.opacity_ = opacity;

  // Define a property to hold the image's div. We'll
  // actually create this div upon receipt of the onAdd()
  // method so we'll leave it null for now.
  this.div_ = null;

  // Explicitly call setMap on this overlay.
  this.setMap(map);
}

/**
 * onAdd is called when the map's panes are ready and the overlay has been
 * added to the map.
 */
ImageOverlayView.prototype.onAdd = function() {
  const div = document.createElement("div");
  div.style.position = "absolute";

  // Create the img element and attach it to the div.
  const img = document.createElement("img");
  img.src = this.image_;
  img.style.width = "100%";
  img.style.height = "100%";
  img.style.position = "absolute";
  img.style.opacity = this.opacity_;
  img.style.transform = `rotate(${this.heading_}deg)`;
  div.appendChild(img);

  this.div_ = div;

  // Add the element to the "overlayLayer" pane.
  const panes = this.getPanes();
  panes.overlayLayer.appendChild(div);
};

ImageOverlayView.prototype.draw = function() {
  // We use the south-west and north-east
  // coordinates of the overlay to peg it to the correct position and size.
  // To do this, we need to retrieve the projection from the overlay.
  const overlayProjection = this.getProjection();

  // Retrieve the south-west and north-east coordinates of this overlay
  // in LatLngs and convert them to pixel coordinates.
  // We'll use these coordinates to resize the div.
  const sw = overlayProjection.fromLatLngToDivPixel(this.bounds_.getSouthWest());
  const ne = overlayProjection.fromLatLngToDivPixel(this.bounds_.getNorthEast());

  // Resize the image's div to fit the indicated dimensions.
  const div = this.div_;
  div.style.left = sw.x + "px";
  div.style.top = ne.y + "px";
  div.style.width = ne.x - sw.x + "px";
  div.style.height = sw.y - ne.y + "px";
};

// The onRemove() method will be called automatically from the API if
// we ever set the overlay's map property to 'null'.
ImageOverlayView.prototype.onRemove = function() {
  try {
    this.div_.parentNode.removeChild(this.div_);
    this.div_ = null;
  } catch (err) {
    // When deleting a marker this._div is already null
    // and will throw "cannot find parentNode of null"
  }
};



// WEBPACK FOOTER //
// ./src/components/ImageOverlay/index.jsx